import React, { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { LoginActions } from 'Redux/modules/login/actions';
import { LoginSelectors } from 'Redux/modules/login/selectors';
import AppConfig from 'Config/AppConfig';
import { useDeviceIntelligenceNewSdk } from 'Hooks/useDeviceIntelligenceNewSdk';
import ApiService from '../../../Services/ApiService';
import NavigationUtils from '../../../Utils/NavigationUtils';
import TwoFactorPanel from './TwoFactorPanel';
import googleIcon from '../../../Assets/google_icon.png';
import styles from './LoginPage.module.less';

// Derive the Google SSO URL.  In production the Django template fills this in;
// here we point to the well-known allauth endpoint.  A real next param is
// forwarded when present in the current URL.
function googleSsoHref() {
  const next = new URLSearchParams(window.location.search).get('next');
  const base = `/${AppConfig.ACCOUNTS_PREFIX}auth/login/google-oauth2/`;
  return next ? `${base}?next=${encodeURIComponent(next)}` : base;
}

function ssoHref() {
  const next = new URLSearchParams(window.location.search).get('next');
  const base = `/${AppConfig.ACCOUNTS_PREFIX}login-with-sso/`;
  return next ? `${base}?next=${encodeURIComponent(next)}` : base;
}

const initialMessages = (window.__INITIAL_STATE && window.__INITIAL_STATE.messages) || [];

const LoginPage = () => {
  const dispatch = useDispatch();

  const reduxIsSubmitting = useSelector(LoginSelectors.loginIsSubmittingSelector);
  const error = useSelector(LoginSelectors.loginErrorSelector);
  const requiresCaptcha = useSelector(LoginSelectors.loginRequiresCaptchaSelector);
  const requires2fa = useSelector(LoginSelectors.loginRequires2faSelector);

  const diApiKey = window.__CONFIG__ && window.__CONFIG__.DEVICE_INTELLIGENCE_NEW_SDK_API_KEY;
  const diApiUrl = window.__CONFIG__ && window.__CONFIG__.DEVICE_INTELLIGENCE_NEW_SDK_API_URL;
  const { getElephantDevice } = useDeviceIntelligenceNewSdk(diApiKey, diApiUrl);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [localIsSubmitting, setLocalIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  const captchaRef = useRef(null);

  // Reset local lock when redux signals the request is done (success or failure).
  useEffect(() => {
    if (!reduxIsSubmitting) setLocalIsSubmitting(false);
  }, [reduxIsSubmitting]);

  const isSubmitting = localIsSubmitting || reduxIsSubmitting;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setLocalIsSubmitting(true);
    const next = new URLSearchParams(window.location.search).get('next');
    // Fall back to the live input values so password-manager autofill (which can
    // skip React's onChange) is still picked up.
    const emailValue = email || e.target.elements.email?.value || '';
    const passwordValue = password || e.target.elements.password?.value || '';
    // Best-effort device token from the DI SDK; null is safe backend-side (spec §9).
    let deviceToken = null;
    try {
      deviceToken = getElephantDevice();
    } catch (err) {
      deviceToken = null;
    }
    dispatch(
      LoginActions.submitLogin({
        email: emailValue,
        password: passwordValue,
        remember,
        captcha: captchaToken,
        deviceToken,
        next,
      }),
    );
  };

  // SSO is a server redirect that can't carry the browser's device token, so stash it in the
  // session first (best-effort) — that lets the SSO-complete handler score the login with Trust.
  const handleSsoClick = (e, href) => {
    e.preventDefault();
    const go = () => {
      window.location = href;
    };
    let deviceToken = null;
    try {
      deviceToken = getElephantDevice();
    } catch (err) {
      deviceToken = null;
    }
    if (!deviceToken) {
      go();
      return;
    }
    ApiService.stashSsoDeviceToken({ deviceToken }).then(go).catch(go);
  };

  const isAutomation = typeof document !== 'undefined' && document.cookie.indexOf('automation_tests') > -1;

  return (
    <>
      <Helmet>
        <title>Sign in to Pipl Search</title>
      </Helmet>

      {requires2fa && <TwoFactorPanel />}

      {!requires2fa && (
        <>
          <h1 className={styles.heading}>Sign in to Pipl Search</h1>
          <p className={styles.subtitle}>Search 5B identities across emails, phones, usernames and social profiles.</p>

          <form id="login-form" onSubmit={handleSubmit} autoCapitalize="off" autoCorrect="off">
            <div className={styles.field}>
              <label htmlFor="id_email" className={styles.label}>
                Work email
              </label>
              <input
                id="id_email"
                name="email"
                type="email"
                maxLength={255}
                placeholder="you@company.com"
                className={styles.input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <span id="required-email-msg" hidden className="text-danger warning">
                This field is required
              </span>
              <span id="invalid-email-msg" hidden className="text-danger warning">
                Please enter a valid Email
              </span>
            </div>

            <div className={styles.field} id="hidePassword">
              <label htmlFor="id_password" className={styles.label}>
                Password
              </label>
              <div className={styles.pwWrapper}>
                <input
                  id="id_password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  className={styles.input}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className={styles.showToggle}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? 'HIDE' : 'SHOW'}
                </button>
              </div>
              <span id="invalid-password-msg" hidden className="text-danger warning">
                This field is required
              </span>
            </div>

            <div className={styles.optionsRow}>
              <label className={styles.checkboxContainer}>
                <input
                  id="id_remember"
                  name="remember"
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                <span className={styles.checkmark} />
                <span className={styles.rememberLabel}>Keep me signed in</span>
              </label>
              <a
                id="reset-password"
                href={`/${AppConfig.ACCOUNTS_PREFIX}password/reset/`}
                className={styles.forgot}
                onClick={(e) => NavigationUtils.navigate(e, `/${AppConfig.ACCOUNTS_PREFIX}password/reset/`)}
              >
                Forgot password?
              </a>
            </div>

            {/* reCAPTCHA — rendered only when backend signals it is required */}
            {requiresCaptcha && !isAutomation && (
              <div className={styles.captcha}>
                <ReCAPTCHA
                  ref={captchaRef}
                  sitekey={AppConfig.RECAPTCHA_KEY || (window.__CONFIG__ && window.__CONFIG__.RECAPTCHA_KEY) || ''}
                  onChange={(token) => setCaptchaToken(token)}
                />
              </div>
            )}

            {/* Server-seeded messages (e.g. "Sorry, your account is inactive.") */}
            {initialMessages.map((m, i) => (
              <div key={i} id="error-div" className={styles.errorText}>
                <span>{m.text}</span>
              </div>
            ))}

            {/* Error display */}
            {error && (
              <div id="invalid-credentials-msg" className={styles.errorText}>
                <span>{error}</span>
              </div>
            )}

            <button type="submit" id="submit-id-login" className={styles.primaryButton} disabled={isSubmitting}>
              Sign in
            </button>

            <div className={styles.divider}>
              <span>or</span>
            </div>

            <a href={googleSsoHref()} onClick={(e) => handleSsoClick(e, googleSsoHref())} className={styles.ssoButton}>
              <img src={googleIcon} width="18" alt="" />
              Continue with Google
            </a>
            <a
              id="oidc_auth"
              href={ssoHref()}
              onClick={(e) => handleSsoClick(e, ssoHref())}
              className={styles.ssoButton}
            >
              Continue with SSO
            </a>
          </form>
        </>
      )}
    </>
  );
};

export default LoginPage;
